import React from 'react';
import PropTypes from 'prop-types';
import Text from "react-native-web/dist/exports/Text";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import { a11yProps, clickProps, getTokensPropType, hrefAttrsProp, linkProps, selectSystemProps, variantProp, viewProps, contentfulProps } from '../utils/props';
import { resolvePressableTokens } from '../utils/pressability';
import { withLinkRouter } from '../utils';
import InlinePressable from './InlinePressable';
import { applyTextStyles, applyOuterBorder, useTheme } from '../ThemeProvider';
import { IconText, iconComponentPropTypes } from '../Icon';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, linkProps, viewProps, contentfulProps]);
const selectOuterBorderStyles = _ref => {
  let {
    outerBorderColor,
    outerBorderWidth,
    outerBorderGap,
    borderRadius,
    outerBorderOutline
  } = _ref;
  return {
    outline: outerBorderOutline,
    ...applyOuterBorder({
      outerBorderColor,
      outerBorderWidth,
      outerBorderGap
    }),
    borderRadius
  };
};
const selectTextStyles = _ref2 => {
  let {
    color,
    blockFontSize
  } = _ref2;
  return {
    color,
    ...applyTextStyles({
      blockFontSize
    }),
    ...Platform.select({
      web: {
        // TODO: https://github.com/telus/universal-design-system/issues/487
        transition: 'color 200ms'
      }
    })
  };
};
const selectBlockStyles = (_ref3, themeOptions) => {
  let {
    blockFontWeight,
    blockFontSize,
    blockLineHeight,
    blockFontName
  } = _ref3;
  return applyTextStyles({
    lineHeight: blockLineHeight,
    fontWeight: blockFontWeight,
    fontSize: blockFontSize,
    fontName: blockFontName,
    themeOptions
  });
};
const selectDecorationStyles = _ref4 => {
  let {
    color,
    textLine,
    textLineStyle,
    alignSelf
  } = _ref4;
  return {
    color,
    textDecorationLine: textLine,
    textDecorationStyle: textLineStyle,
    alignSelf,
    ...Platform.select({
      web: {
        // TODO: https://github.com/telus/universal-design-system/issues/487
        transition: 'color 200ms'
      }
    })
  };
};
const selectIconTokens = _ref5 => {
  let {
    color,
    iconSize,
    blockFontSize,
    iconTranslateX,
    iconTranslateY
  } = _ref5;
  /**
   * These calculations were carried out using a set of linear equations to calculate that the
   * position of the icon "->"" is aligned to the first line of the tooltip text.
   * The base equation is: X/4 + Y/4 - 4 - |X - Y| = Z
   * where X = blockFontSize, Y = iconSize and Z = translateY
   */
  const translateY = iconTranslateY && iconTranslateY !== 0 ? iconTranslateY : blockFontSize / 4 + iconSize / 4 - 4 - Math.abs(iconSize - blockFontSize);
  return {
    color,
    translateX: iconTranslateX,
    translateY,
    size: iconSize
  };
};

/**
 * Renders a pressable text link, with optional icon. This is rendered as a block element
 * only (see 'Current Limitations'). On Web, renders an `<a>` anchor tag if `href` is provided.
 *
 * ## Current limitations
 *
 * - Not yet integrated with widely-used Telus navigation packages. This will probably
 *   require adding a `navigate` prop that aliases onPress so this can be passed to
 *   router's Link components' `Component` or `as` props but this needs testing
 *   as part of https://github.com/telus/universal-design-system/issues/24
 *
 * - Doesn't work inline e.g. in paragraphs. Support for inline links in native and
 *   web was implemented in https://github.com/telus/universal-design-system/pull/233
 *   but removed and will need to be restored at some point. When inline link support
 *   is restored, icons in inline links on Android will be slightly off-centre
 *   vertically (see )
 *
 * - Doesn't support :visited styles on web. Support for :visited styles was
 *   implemented in https://github.com/telus/universal-design-system/pull/234 but
 *   dropped in favour of investigating if a full-featured CSS-in-JS package could or
 *   should be used more widely (e.g. styled components)
 */
const LinkBase = /*#__PURE__*/React.forwardRef((_ref6, ref) => {
  let {
    href,
    icon,
    iconPosition = icon ? 'left' : undefined,
    iconProps = {},
    variant,
    tokens = {},
    children,
    dataSet,
    accessibilityRole = 'link',
    ...rawRest
  } = _ref6;
  const {
    onPress,
    ...props
  } = clickProps.toPressProps(rawRest);
  const {
    hrefAttrs,
    rest
  } = hrefAttrsProp.bundle(props);
  const selectedProps = selectProps({
    accessibilityRole,
    href,
    onPress: linkProps.handleHref({
      href,
      onPress
    }),
    hrefAttrs,
    ...rest
  });
  const resolveLinkTokens = pressState => resolvePressableTokens(tokens, pressState, {
    iconPosition
  });
  const defaultThemeTokens = resolveLinkTokens({});
  const hasIcon = Boolean(icon || defaultThemeTokens.icon);

  // On web, this makes focus rings wrap only the link, not the entire block
  const blockLeftStyle = Platform.OS === 'web' && staticStyles.blockLeft;
  const {
    themeOptions
  } = useTheme();
  return /*#__PURE__*/_jsx(InlinePressable, {
    ...selectedProps,
    dataSet: dataSet,
    inlineFlex: hasIcon
    // assuming links without icons should be inline (even if they are long)
    ,
    ref: ref,
    style: linkState => {
      const themeTokens = resolveLinkTokens(linkState);
      const outerBorderStyles = selectOuterBorderStyles(themeTokens);
      const decorationStyles = selectDecorationStyles(themeTokens);
      const mobileCompensation = null;
      return [outerBorderStyles, mobileCompensation, blockLeftStyle, decorationStyles, hasIcon && staticStyles.rowContainer];
    },
    children: linkState => {
      const themeTokens = resolveLinkTokens(linkState);
      const textStyles = selectTextStyles(themeTokens);
      const iconTokens = selectIconTokens(themeTokens);
      // TODO: may need to apply some smarter text inheritance here if inline to avoid native
      // issues like double-application of line heights breaking align-items: baseline
      const blockTextStyles = selectBlockStyles(themeTokens, themeOptions);
      const IconComponent = icon || themeTokens.icon;
      const {
        iconSpace
      } = themeTokens;
      const isTextOnlyLink = !IconComponent && !icon && accessibilityRole === 'link';
      const adjustedIconSpace = Platform.OS !== 'web' && isTextOnlyLink ? 0 : iconSpace;
      return /*#__PURE__*/_jsx(IconText, {
        icon: IconComponent,
        iconPosition: iconPosition,
        space: adjustedIconSpace,
        iconProps: {
          ...iconProps,
          tokens: iconTokens,
          style: staticStyles.bubblePointerEvents
        },
        children: /*#__PURE__*/_jsx(Text, {
          style: [textStyles, blockTextStyles, staticStyles.baseline, staticStyles.bubblePointerEvents],
          children: typeof children === 'function' ? children(linkState) : children
        })
      });
    }
  });
});
LinkBase.displayName = 'LinkBase';
LinkBase.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('Link'),
  /**
   * href for the Link
   */
  href: PropTypes.string,
  /**
   * AccesibilityRole for the link base
   */
  accessibilityrole: PropTypes.string,
  /**
   * Children nodes that can be added
   */
  variant: variantProp.propType,
  /**
   * Optional variant that may be passed down to the link's icon if there is one
   */
  iconProps: PropTypes.exact(iconComponentPropTypes),
  /**
   * A function component for an SVG icon to render inside the link. Inherits size and color from
   * the link and any Typography the link is nested inside.
   */
  icon: PropTypes.elementType,
  /**
   * When `icon` is provided, use `iconPosition` to place the Icon to the left, right or inline with Link.
   */
  iconPosition: PropTypes.oneOf(['left', 'right', 'inline']),
  /**
   * On Web if href is passed, React Native Web maps this object's props to
   * `rel`, `target` and `download` attrs.
   */
  hrefAttrs: PropTypes.shape(hrefAttrsProp.types)
};
const staticStyles = StyleSheet.create({
  blockLeft: {
    alignSelf: 'flex-start'
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  baseline: {
    alignSelf: 'baseline'
  },
  bubblePointerEvents: {
    ...Platform.select({
      web: {
        pointerEvents: 'none'
      }
    })
  },
  outerBorderCompensation: {
    ...(Platform.OS !== 'web' && {
      marginHorizontal: 2,
      paddingHorizontal: Platform.OS === 'android' ? 2 : 0,
      paddingTop: Platform.OS === 'android' ? 2 : 0
    })
  }
});
export default withLinkRouter(LinkBase);